import { useEffect, useState } from "react";
import { problemaDeEdad } from "../../../../utils/edadParticipante";
import {
  idDelValor,
  nombreEnLista,
  valorConId,
  valorDeComuna,
} from "../../../../utils/valorDeUbicacion";
// hooks
import { usePersonaNatural } from "../../../../hooks/usePersonaNatural";
// styled components
import {
  Button,
  Select,
  Form,
  Input,
  DatePicker,
} from "antd";
// dataSelect
import {
  SexoOptions,
  TipoDocumentosOptions,
  EstratoOptions,
  OrientacionSexualoOptions,
  IdentidadGeneroOptions,
  comunasOptions,
} from "../../../../utils/dataSelectOptions";
// style
import "../../Usuario.style.scss";
import { useLocations } from "../../../../hooks/useLocations";
import { InputNumero } from "../../../../components/Mascaras/InputNumero/InputNumero";
import { InputTexto } from "../../../../components/Mascaras/InputTexto/InputTexto";
import moment from "moment";
import { openNotification } from "../../../../utils/saveFile";
import { useNavigate } from "react-router-dom";
import { ModalDireccion } from "../../../../components/ModalDireccion/ModalDireccion";
import apiLocation from "../../../../api/Participantes/api.location";
// Models
import { Convocatoria } from "../../../../models/ConvocatoriasModel/convocatoria.model";
import { Municipio } from "../../../../models/LocationsModels/locations.models";

import locale from 'antd/es/date-picker/locale/es_ES';
import apiMaestras from "../../../../api/Maestras/api-maestras";

interface IProps {
  convocatorias?: Convocatoria;
  /**
   * Se avisa al salir de cualquier campo, para que quien monte el formulario
   * pueda guardar lo escrito. Es opcional: sin el, el formulario se comporta
   * como siempre.
   */
  alSalirDeUnCampo?: (participante: any) => void;
  /**
   * La botonera propia del formulario, que lleva a la siguiente pagina del
   * flujo por pasos. El wizard trae la suya y no quiere que lo saquen de el.
   */
  mostrarNavegacion?: boolean;
  /**
   * Persona natural y grupo conformado llenan el mismo formulario. Solo
   * cambian dos cosas: el grupo pide ademas el pais de nacimiento, y guarda
   * con otra funcion porque necesita dejar a mano el id del participante.
   */
  perfil?: "natural" | "grupo";
}

export const FormPersonaNatural = (props: IProps) => {
  const navigate = useNavigate();
  // hook
  const {
    infoparticipante,
    setInforParticipante,
    cargarInformacionParticipante,
    guardarInformacionParticipante,
    guardarInformacionParticipanteGrupo,
    loading,
  } = usePersonaNatural();

  const esGrupo = props.perfil === "grupo";
  const guardarDatosPersonales = esGrupo
    ? guardarInformacionParticipanteGrupo
    : guardarInformacionParticipante;
  const { paises, departamentos } = useLocations();

  //states
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  // Va aqui porque el arreglo de campos lo usa para resolver el barrio.
  const [barrios, setBarrios] = useState([]);
  /**
   * La comuna y el barrio solo aplican a Medellin.
   *
   * Antes esto era un estado que solo encendia elegir el municipio, asi que
   * al volver a entrar con MEDELLIN ya guardado los campos ni se dibujaban.
   * Se deduce del municipio: no hay un estado que pueda quedar desfasado.
   *
   * Se compara por contenido porque el valor puede venir como nombre suelto
   * o como "id-nombre", segun lo escriba el guardado o el select.
   */
  const visibleFields = String(infoparticipante.municipio_nombre ?? "").includes(
    "MEDELLÍN"
  );
  const [openModal, setOpenModal] = useState(false);
  const [guardarFormulario, setGuardarFormulario] = useState(false);

  // form fields
  const fields = [
    { name: "numero_documento", value: infoparticipante.numero_documento },
    {
      name: "tipo_identificacion",
      value: infoparticipante.tipo_identificacion,
    },
    { name: "primer_nombre", value: infoparticipante.primer_nombre },
    { name: "segundo_nombre", value: infoparticipante.segundo_nombre },
    { name: "primer_apellido", value: infoparticipante.primer_apellido },
    { name: "segundo_apellido", value: infoparticipante.segundo_apellido },
    { name: "fecha_nacimiento", value: infoparticipante.fecha_nacimiento },
    { name: "sexo", value: infoparticipante.sexo },
    { name: "orientacion", value: infoparticipante.orientacion },
    { name: "identidad", value: infoparticipante.identidad },
    { name: "indicativo", value: infoparticipante.indicativo },
    { name: "etnia", value: infoparticipante.etnia },
    { name: "telefono_celular", value: infoparticipante.telefono_celular },
    {
      name: "direccion_residencia",
      value: infoparticipante.direccion_residencia,
    },
    { name: "pais_nacimiento", value: infoparticipante.pais_nacimiento },
    { name: "pais_residencia", value: infoparticipante.pais_residencia },
    { name: "departamento", value: infoparticipante.departamento },
    {
      // Se guarda el nombre, pero la opcion del select vale "id-nombre":
      // sin traducirlo el campo salia en blanco al volver a entrar.
      name: "departamento_nombre",
      value: valorConId(departamentos, infoparticipante.departamento_nombre),
    },
    { name: "municipio_residencia", value: infoparticipante.municipio },
    {
      name: "municipio_nombre",
      value: valorConId(municipios, infoparticipante.municipio_nombre),
    },
    { name: "comuna", value: valorDeComuna(comunasOptions, infoparticipante.comuna) },
    // Se guarda en mayusculas y la opcion vale lo que trae el servicio.
    {
      name: "barrio_nombre",
      value: nombreEnLista(barrios, infoparticipante.barrio_nombre),
    },
    { name: "estrato", value: infoparticipante.estrato },
    { name: "correo_electronico", value: infoparticipante.correo_electronico },
  ];

  /**
   * Guarda el barrio elegido.
   *
   * Antes se partia el valor por el guion y se guardaba solo lo de delante,
   * seguramente de cuando valia "id-nombre". Hoy la opcion vale el nombre a
   * secas, asi que partirlo truncaba los que llevan guion dentro:
   * "Corazón De Jesús - Barrio Triste" quedaba en "Corazón De Jesús".
   */
  const setBarrio = (nombre: string) => {
    setInforParticipante({
      ...infoparticipante,
      barrio_nombre: nombre,
      barrio: String(nombre ?? "").toUpperCase(),
    });
  };

  const handleDepartamento = async (departamento: string) => {
    try {
      setMunicipios([]);
      const cob =
        props.convocatorias?.cobertura == "Área"
          ? "Área Metropolitana"
          : props.convocatorias?.cobertura;
      openNotification({
        titulo: "",
        descripcion:
          "Recuerda que la cobertura de esta convocatoria es de tipo " + cob,
        placement: "bottomRight",
      });

      let nombreSeparado = departamento.split("-");

      setInforParticipante({
        ...infoparticipante,
        departamento_nombre: `${nombreSeparado[1]}`,
        departamento: `${nombreSeparado[1]}`,
      });

      const municipios = await apiLocation.obtenerMunicipios(nombreSeparado[0]);
      return setMunicipios(municipios);
    } catch (error) {
      return console.error(error);
    }
  };

  const handleMunicipio = async (municipio: string) => {
    try {
      setBarrios([]);
      let nombreSeparado = municipio.split("-");

      setInforParticipante({
        ...infoparticipante,
        municipio_nombre: `${nombreSeparado[1]}`,
        municipio: `${nombreSeparado[1]}`,
      });

      // La comuna y el barrio se muestran solos: dependen del municipio.
      return;
    } catch (error) {
      return console.log(error);
    }
  };
  const handleComunas = async (id_comunas: string) => {
    localStorage.setItem("comuna", id_comunas);
    // Actualizar la comuna en el estado de forma explícita para evitar
    // race condition con onFieldsChange (stale closure).
    setInforParticipante((prev) => ({ ...prev, comuna: id_comunas }));
    try {
      let nombreSeparado = id_comunas.split("-");
      const barrios = await apiLocation.obtenerBarrios(nombreSeparado[0]);
      setBarrios(barrios);
    } catch (error) {
      console.log(error);
    }
  };

  // Convocatorias
  const [nombreLinea, setNombreLinea] = useState('');

  /**
   * Lo que las reglas de edad necesitan saber: si la convocatoria admite
   * menores, y en que linea se presenta, de donde salen los rangos propios
   * de algunas lineas de la Secretaria de las Mujeres.
   */
  const contextoDeEdad = {
    aceptaMenores: Boolean(props.convocatorias?.menor_edad),
    nombreLinea,
  };


  /**
   * Carga las listas que dependen de lo que ya estaba guardado.
   *
   * Los municipios solo se pedian al elegir un departamento, y los barrios
   * al elegir una comuna. Quien volvia a entrar encontraba el municipio en
   * blanco y deshabilitado, porque su lista nunca se habia pedido.
   */
  useEffect(() => {
    let vigente = true;

    const cargarListasDependientes = async () => {
      // Se busca el departamento en la lista en vez de partir su valor: si
      // todavia no ha llegado, valorConId devuelve el nombre tal cual y se
      // habrian pedido los municipios de "ANTIOQUIA" en vez de los de "05".
      const suyo = (Array.isArray(departamentos) ? departamentos : []).find(
        (x: any) => x?.nombre === infoparticipante.departamento_nombre
      );
      const idDepartamento = suyo?.id;
      if (!idDepartamento || municipios.length > 0) return;

      try {
        const lista = await apiLocation.obtenerMunicipios(idDepartamento);
        if (vigente) setMunicipios(lista);
      } catch (error) {
        console.error("No se pudieron cargar los municipios guardados", error);
      }
    };

    const cargarBarriosGuardados = async () => {
      // La comuna se guarda como "10-La Candelaria": el id va delante.
      const idComuna = idDelValor(infoparticipante.comuna);
      if (!idComuna || barrios.length > 0) return;

      try {
        const lista = await apiLocation.obtenerBarrios(idComuna);
        if (vigente) setBarrios(lista);
      } catch (error) {
        console.error("No se pudieron cargar los barrios guardados", error);
      }
    };

    cargarListasDependientes();
    cargarBarriosGuardados();

    return () => {
      vigente = false;
    };
  }, [departamentos, infoparticipante.departamento_nombre, infoparticipante.comuna]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      cargarInformacionParticipante();
      const dataLineas = await apiMaestras.obtenerLineaConvocatoria();
      if (mounted) {
        const valueFilter = dataLineas.find((x: any) => x.id === props.convocatorias?.linea_convocatoria);
        const linea = valueFilter?.nombre || "";
        setNombreLinea(linea);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [props.convocatorias?.linea_convocatoria]);

  return (
    <div onBlur={() => props.alSalirDeUnCampo?.(infoparticipante)}>
    <Form
      name="basic"
      size="large"
      layout="vertical"
      fields={fields}
      onFinish={() => {
        const problema = problemaDeEdad(
          infoparticipante.fecha_nacimiento,
          contextoDeEdad
        );
        if (problema) {
          return openNotification({
            titulo: "Error",
            descripcion: problema,
            placement: "bottomRight",
          });
        }

        // Dentro del wizard no se navega: lo escrito ya se guarda solo al
        // salir de cada campo, y cambiar de pagina perderia el paso.
        if (props.mostrarNavegacion === false) {
          props.alSalirDeUnCampo?.(infoparticipante);
          return;
        }

        guardarDatosPersonales(guardarFormulario);
      }}
      onFieldsChange={(_, fields: any) => {
        let infoupdate: any = { ...infoparticipante };
        for (const i in fields) {
          infoupdate[fields[i].name] = fields[i].value;
        }
        setInforParticipante(infoupdate);
      }}
    >
      <div className="container-fluid2 mt-4">
        <div className="row">
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Tipo de documento"
              name="tipo_identificacion"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select
                style={{ width: "100%" }}
                placeholder="C.C."
                disabled={true}
              >
                {TipoDocumentosOptions.map(({ value, text }) => (
                  <Select.Option key={`TIPODOC-${value}`} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Número de documento"
              name="numero_documento"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <InputNumero
                name="numero_documento"
                texto={infoparticipante.numero_documento}
                setTexto={setInforParticipante}
                alltexto={infoparticipante}
                disabled={true}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Primer nombre"
              name="primer_nombre"
              rules={[{ required: true, message: "Campo obligatorio" }]}
              required
            >
              <InputTexto
                maxLength={100}
                name="primer_nombre"
                texto={infoparticipante.primer_nombre}
                setTexto={setInforParticipante}
                alltexto={infoparticipante}
                onInput={infoparticipante.primer_nombre}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              name="segundo_nombre"
              label={
                <>
                  Segundo nombre{" "}
                  <span className="font-family-Montserrat-Regular font-size-10px font-color-707070">
                    {" "}
                    - Opcional
                  </span>
                </>
              }
            >
              <InputTexto
                maxLength={100}
                name="segundo_nombre"
                texto={infoparticipante.segundo_nombre}
                setTexto={setInforParticipante}
                alltexto={infoparticipante}
                onInput={infoparticipante.segundo_nombre}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Primer apellido"
              name="primer_apellido"
              rules={[{ required: true, message: "" }]}
            >
              <InputTexto
                maxLength={100}
                name="primer_apellido"
                texto={infoparticipante.primer_apellido}
                setTexto={setInforParticipante}
                alltexto={infoparticipante}
                onInput={infoparticipante.primer_apellido}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              name="segundo_apellido"
              label={
                <>
                  Segundo apellido{" "}
                  <span className="font-family-Montserrat-Regular font-size-10px font-color-707070">
                    {" "}
                    - Opcional
                  </span>
                </>
              }
            >
              <InputTexto
                maxLength={100}
                name="segundo_apellido"
                texto={infoparticipante.segundo_apellido}
                setTexto={setInforParticipante}
                alltexto={infoparticipante}
                onInput={infoparticipante.segundo_apellido}
              />
            </Form.Item>
          </div>

          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Fecha de nacimiento"
              name="fecha_nacimiento"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <DatePicker
                locale={locale}
                placeholder="fecha de nacimiento"
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                onChange={(value) => {
                  // Las reglas viven en edadParticipante, con sus pruebas.
                  const problema = problemaDeEdad(value, contextoDeEdad);
                  if (!problema) return;

                  setInforParticipante({
                    ...infoparticipante,
                    fecha_nacimiento: "",
                  });
                  return openNotification({
                    titulo: "Error",
                    descripcion: problema,
                    placement: "bottomRight",
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Género"
              name="sexo"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Seleccionar...">
                {SexoOptions.map(({ value, text }) => (
                  <Select.Option key={`SEXO-${value}`} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label={
                <>
                  Orientación sexual
                </>
              }
              name="orientacion"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Seleccionar...">
                {OrientacionSexualoOptions.map(({ value, text }) => (
                  <Select.Option key={value} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label={
                <>
                  Discapacidad
                </>
              }
              name="identidad"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Seleccionar...">
                {IdentidadGeneroOptions.map(({ value, text }) => (
                  <Select.Option key={value} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Etnia"
              name="etnia"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Seleccionar...">
                <Select.Option key="Palenqueros" value="Palenqueros">
                  Palenqueros
                </Select.Option>
                <Select.Option key="Comunidades" value="Comunidades">
                  Comunidades Negras o Afrocolombianas
                </Select.Option>
                <Select.Option key="Comunidad" value="Comunidad">
                  Comunidad raizal
                </Select.Option>
                <Select.Option key="Pueblos" value="Pueblos">
                  Pueblos y Comunidades Indígenas
                </Select.Option>
                <Select.Option key="Pueblo" value="Pueblo">
                  Pueblo
                </Select.Option>
                <Select.Option key="Ninguno" value="Ninguno">
                  Ninguno
                </Select.Option>
              </Select>
            </Form.Item>
          </div>
        </div>
      </div>
      <div className="container-fluid2 mt-5">
        <div className="row">
          <div className="col-12">
            <div className=" font-family-Montserrat-Regular font-size-18px titulo-numero-postulacion2">
              Datos de contacto
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid2 mt-3">
        <div className="row">
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Número de contacto"
              name="telefono_celular"
              rules={[
                { required: true, message: "Campo obligatorio" },
                { min: 10, message: "Mínimo 10 números" },
                { max: 10, message: "Máximo 10 números" },
              ]}
            >
              <Input
                maxLength={10}
                style={{ width: "100%" }}
                placeholder="Número:"
                onChange={(e) => {
                  setInforParticipante({
                    ...infoparticipante,
                    telefono_celular: e.target.value.replace(/[^0-9]/g, ""),
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Dirección de residencia"
              name="direccion_residencia"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Input
                maxLength={100}
                readOnly={true}
                style={{ width: "100%" }}
                onClick={() => {
                  if (infoparticipante.modalState) {
                    setOpenModal(true);
                  }
                }}
              />
            </Form.Item>
          </div>
          {esGrupo && (
            <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
              <Form.Item
                label="País de nacimiento"
                name="pais_nacimiento"
                rules={[{ required: true, message: "Campo obligatorio" }]}
              >
                <Select showSearch style={{ width: "100%" }} placeholder="Seleccionar...">
                  {paises.map(({ id, nombre }) => (
                    <Select.Option key={id} value={nombre}>
                      {nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="País de residencia"
              name="pais_residencia"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select showSearch style={{ width: "100%" }} placeholder="Seleccionar...">
                {paises.map(({ id, nombre }) => (
                  <>
                    {nombre === "COLOMBIA" && (
                      <Select.Option key={id} value={nombre}>
                        {nombre}
                      </Select.Option>
                    )}
                  </>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Departamento de residencia"
              name="departamento_nombre"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select
                // showSearch
                // filterOption={(input, option: any) =>
                //   option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                // }
                // filterSort={(optionA, optionB) =>
                //   optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                // }
                style={{ width: "100%" }}
                placeholder="Seleccionar..."
                onChange={handleDepartamento}
              >
                {departamentos.map(({ id, nombre }) => (
                  <>
                    {(props.convocatorias?.cobertura == "Municipal" ||
                      props.convocatorias?.cobertura == "Área") &&
                      nombre == "ANTIOQUIA" && (
                        <Select.Option key={id} value={`${id}-${nombre}`}>
                          {nombre}
                        </Select.Option>
                      )}
                    {props.convocatorias?.cobertura != "Municipal" &&
                      props.convocatorias?.cobertura != "Área" && (
                        <Select.Option key={id} value={`${id}-${nombre}`}>
                          {nombre}
                        </Select.Option>
                      )}
                  </>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Municipio de residencia"
              required
              name="municipio_nombre"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select
                placeholder="Seleccionar"
                onChange={handleMunicipio}
                disabled={municipios.length === 0 ? true : false}
              // showSearch
              // filterOption={(input, option: any) =>
              //   option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              // }
              // filterSort={(optionA, optionB) =>
              //   optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
              // }
              >
                {municipios?.map(({ id, nombre }) => (
                  <>
                    {props.convocatorias?.cobertura == "Municipal" &&
                      nombre == "MEDELLÍN" && (
                        <Select.Option key={id} value={`${id}-${nombre}`}>
                          {nombre}
                        </Select.Option>
                      )}
                    {props.convocatorias?.cobertura != "Municipal" && (
                      <Select.Option key={id} value={`${id}-${nombre}`}>
                        {nombre}
                      </Select.Option>
                    )}
                  </>
                ))}
              </Select>
            </Form.Item>
          </div>

          {visibleFields && (
            <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
              <Form.Item
                label="Comuna de residencia"
                name="comuna"
                rules={[
                  infoparticipante.municipio_nombre === "MEDELLÍN"
                    ? { required: true, message: "Campo obligatorio" }
                    : {},
                ]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder="Seleccionar..."
                  onChange={handleComunas}
                // showSearch
                // filterOption={(input, option: any) =>
                //   option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                // }
                // filterSort={(optionA, optionB) =>
                //   optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                // }
                >
                  {comunasOptions.map(({ key, value }) => (
                    <Select.Option key={key} value={`${key}-${value}`}>
                      {key} - {value}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}

          {visibleFields && (
            <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
              <Form.Item
                label="Barrio de residencia"
                name="barrio_nombre"
                rules={[
                  infoparticipante.municipio_nombre === "MEDELLÍN" &&
                  barrios.length > 0 && {
                    required: true,
                    message: "Campo obligatorio",
                  },
                ]}
              >
                <Select
                  // showSearch
                  // filterOption={(input, option: any) =>
                  //   option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  // }
                  // filterSort={(optionA, optionB) =>
                  //   optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  // }
                  onChange={(e: string) => setBarrio(e)}
                >
                  {barrios?.map(({ id, nombre }) => (
                    <Select.Option key={id} value={nombre}>
                      {nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}

          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Estrato"
              name="estrato"
              rules={[{ required: true, message: "Campo obligatorio" }]}
            >
              <Select style={{ width: "100%" }} placeholder="Seleccionar...">
                {EstratoOptions.map(({ value, text }) => (
                  <Select.Option key={`ESTRATO-${value}`} value={value}>
                    {text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="col-12 col-md-6 col-lg-3 col-xxl-3">
            <Form.Item
              label="Correo electrónico"
              name="correo_electronico"
              rules={[
                {
                  type: "email",
                  message: "",
                },
                {
                  required: true,
                  message: "Campo obligatorio",
                },
              ]}
            >
              <Input maxLength={100} style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </div>
      </div>
      {(props.mostrarNavegacion ?? true) && (
      <div
        style={{
          width: "100%",
          justifyContent: "center",
          marginTop: "0.5%",
          position: "sticky",
          bottom: 0,
          paddingBottom: "0",
        }}
      >
        <div className="container-fluid2 pe-md-0 bg-white justify-content-flex-end">
          <Button
            style={{ marginRight: "16px" }}
            htmlType="submit"
            className="button-blanco-radius font-size-12px"
            onClick={() => setGuardarFormulario(true)}
            disabled={loading}
          >
            Guardar y salir
            {loading && (
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: 12, marginLeft: 4, color: "#fff" }}
              />
            )}
          </Button>
          <Button
            style={{ marginRight: "16px" }}
            className="button-blanco-radius font-size-12px"
            onClick={() => navigate(-1)}
          >
            Atrás
          </Button>
          <Button
            className="button-azul-radius font-size-12px"
            htmlType="submit"
            name="basic"
            disabled={loading}
            onClick={() => setGuardarFormulario(false)}
          >
            Continuar
            {loading && (
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: 12, marginLeft: 4, color: "#fff" }}
              />
            )}
          </Button>
        </div>
      </div>
      )}
      <ModalDireccion
        modal={openModal}
        CancelarModal={() => setOpenModal(false)}
        AceptarModal={() => setOpenModal(false)}
        directions={infoparticipante}
        setDireccion={setInforParticipante}
        namedirections="direccion_residencia"
      />
    </Form>
    </div>
  );
};
